import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Filter,
  RefreshCw,
  Award,
  Calendar,
  AlertTriangle,
  Pencil,
  Trash2,
  X,
  ExternalLink,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import { toast } from '@/app/components/ui/simple-toaster';
import { QuickAddSelect } from '@/components/shared/quick-add-select';
import {
  useCertifications,
  useCreateCertification,
  useUpdateCertification,
  useDeleteCertification,
  type CertificationWithDetails,
} from '@/hooks/use-certifications';
import { useAssets, useCreateAsset } from '@/hooks/use-assets';
import { useOrgToday } from '@/hooks/use-settings';
import { formatDateOnly } from '@/lib/date-utils';
import type { Asset, AssetType, CertificationType, CreateCertificationPayload } from '@/lib/types';
import { resolveMediaUrl } from '@/lib/api-client';
import { usePermissions } from '@/hooks/use-permissions';

const typeColors: Record<CertificationType, string> = {
  CE: 'bg-blue-100 text-blue-800',
  ANSI: 'bg-green-100 text-green-800',
  CSA: 'bg-purple-100 text-purple-800',
  custom: 'bg-gray-100 text-gray-800',
};

const ASSET_TYPE_OPTIONS: { value: AssetType; label: string }[] = [
  { value: 'harness', label: 'Harness' },
  { value: 'lanyard', label: 'Lanyard' },
  { value: 'connector', label: 'Connector' },
  { value: 'carabiner', label: 'Carabiner' },
  { value: 'rope', label: 'Rope' },
  { value: 'sling', label: 'Sling' },
  { value: 'descender', label: 'Descender' },
  { value: 'ascender', label: 'Ascender' },
  { value: 'pulley', label: 'Pulley' },
  { value: 'helmet', label: 'Helmet' },
  { value: 'anchor', label: 'Anchor' },
  { value: 'anchor_strap', label: 'Anchor Strap' },
  { value: 'fall_arrester', label: 'Fall Arrester' },
  { value: 'fall_arrest_accessory', label: 'Fall Arrest Accessory' },
  { value: 'mobile_fall_arrester', label: 'Mobile Fall Arrester' },
  { value: 'rigging_plate', label: 'Rigging Plate' },
  { value: 'rope_clamp', label: 'Rope Clamp' },
  { value: 'swivel', label: 'Swivel' },
  { value: 'other', label: 'Other' },
];

function InlineCreateAssetForm({
  onCreated,
  onCancel,
}: {
  onCreated: (asset: Asset) => void;
  onCancel: () => void;
}) {
  const [serialNumber, setSerialNumber] = useState('');
  const [type, setType] = useState<AssetType>('other');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [error, setError] = useState('');
  const createAssetMutation = useCreateAsset();

  const handleCreate = useCallback(async () => {
    const trimmedSerialNumber = serialNumber.trim();
    const trimmedManufacturer = manufacturer.trim();
    const trimmedModel = model.trim();

    if (!trimmedSerialNumber) {
      setError('Serial number is required');
      return;
    }
    if (!trimmedManufacturer) {
      setError('Manufacturer is required');
      return;
    }
    if (!trimmedModel) {
      setError('Model is required');
      return;
    }

    setError('');
    try {
      const asset = await createAssetMutation.mutateAsync({
        serial_number: trimmedSerialNumber,
        type,
        manufacturer: trimmedManufacturer,
        model: trimmedModel,
      });
      onCreated(asset);
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : 'Failed to create asset');
    }
  }, [createAssetMutation, manufacturer, model, onCreated, serialNumber, type]);

  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label htmlFor="quick-asset-serial">Serial number *</Label>
        <Input
          id="quick-asset-serial"
          placeholder="Enter serial number"
          value={serialNumber}
          onChange={(event) => setSerialNumber(event.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="quick-asset-type">Type *</Label>
        <Select value={type} onValueChange={(value) => setType(value as AssetType)}>
          <SelectTrigger id="quick-asset-type">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {ASSET_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="quick-asset-manufacturer">Manufacturer *</Label>
        <Input
          id="quick-asset-manufacturer"
          placeholder="Enter manufacturer"
          value={manufacturer}
          onChange={(event) => setManufacturer(event.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="quick-asset-model">Model *</Label>
        <Input
          id="quick-asset-model"
          placeholder="Enter model"
          value={model}
          onChange={(event) => setModel(event.target.value)}
        />
      </div>

      {error ? <p className="text-sm text-red-500">{error}</p> : null}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={createAssetMutation.isPending}>
          Cancel
        </Button>
        <Button type="button" onClick={handleCreate} disabled={createAssetMutation.isPending}>
          Create asset
        </Button>
      </div>
    </div>
  );
}

const PAGE_SIZE = 20;

interface CertificationFormData {
  asset: number | '';
  cert_type: CertificationType;
  issue_date: string;
  expiry_date: string;
  issuer: string;
  document_url: string;
}

const getInitialFormData = (orgToday: string): CertificationFormData => ({
  asset: '',
  cert_type: 'CE',
  issue_date: orgToday,
  expiry_date: '',
  issuer: '',
  document_url: '',
});

export function CertificationsPage() {
  // `admin_or_inspector` is seeded to Inspection Certified people, which the
  // profile reports as `can_record_inspections`.
  const { canRecordInspections: canAddCertifications } = usePermissions();
  const orgToday = useOrgToday();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCertification, setSelectedCertification] = useState<CertificationWithDetails | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [formData, setFormData] = useState<CertificationFormData>(() => getInitialFormData(orgToday));

  const page = parseInt(searchParams.get('page') || '1', 10);
  const typeFilter = (searchParams.get('cert_type') || '') as CertificationType | '';
  const assetId = searchParams.get('asset') || '';
  const expiryStatus = searchParams.get('is_expired') || '';

  const { data, isLoading, isError, refetch } = useCertifications({
    page,
    page_size: PAGE_SIZE,
    cert_type: typeFilter,
    asset: assetId ? parseInt(assetId, 10) : '',
    is_expired: expiryStatus === 'true' ? true : expiryStatus === 'false' ? false : '',
  });

  const { data: assetsData, isLoading: isAssetsLoading } = useAssets({ page_size: 100 });
  const createMutation = useCreateCertification();
  const updateMutation = useUpdateCertification();
  const deleteMutation = useDeleteCertification();

  const certifications = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const expiredCount = certifications.filter((c) => c.is_expired).length;
  const validCount = certifications.filter((c) => !c.is_expired).length;

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const openCreateDialog = () => {
    setFormData(getInitialFormData(orgToday));
    setSelectedAsset(null);
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (certification: CertificationWithDetails) => {
    setSelectedCertification(certification);
    setFormData({
      asset: certification.asset,
      cert_type: certification.cert_type,
      issue_date: certification.issue_date,
      expiry_date: certification.expiry_date,
      issuer: certification.issuer,
      document_url: certification.document_url || '',
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (certification: CertificationWithDetails) => {
    setSelectedCertification(certification);
    setIsDeleteDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!formData.asset) {
      toast.error('Please select an asset');
      return;
    }
    if (!formData.issue_date) {
      toast.error('Issue date is required');
      return;
    }
    if (!formData.expiry_date) {
      toast.error('Expiry date is required');
      return;
    }
    if (!formData.issuer.trim()) {
      toast.error('Issuer is required');
      return;
    }

    try {
      const payload: CreateCertificationPayload = {
        asset: formData.asset as number,
        cert_type: formData.cert_type,
        issue_date: formData.issue_date,
        expiry_date: formData.expiry_date,
        issuer: formData.issuer.trim(),
      };
      if (formData.document_url.trim()) {
        payload.document_url = formData.document_url.trim();
      }

      await createMutation.mutateAsync(payload);
      toast.success('Certification created successfully');
      setIsCreateDialogOpen(false);
      setFormData(getInitialFormData(orgToday));
    } catch (_error) {
      toast.error('Failed to create certification');
    }
  };

  const handleUpdate = async () => {
    if (!selectedCertification) return;
    if (!formData.asset) {
      toast.error('Please select an asset');
      return;
    }
    if (!formData.issue_date) {
      toast.error('Issue date is required');
      return;
    }
    if (!formData.expiry_date) {
      toast.error('Expiry date is required');
      return;
    }
    if (!formData.issuer.trim()) {
      toast.error('Issuer is required');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: selectedCertification.id,
        data: {
          asset: formData.asset as number,
          cert_type: formData.cert_type,
          issue_date: formData.issue_date,
          expiry_date: formData.expiry_date,
          issuer: formData.issuer.trim(),
          document_url: formData.document_url.trim() || undefined,
        },
      });
      toast.success('Certification updated successfully');
      setIsEditDialogOpen(false);
      setSelectedCertification(null);
      setFormData(getInitialFormData(orgToday));
    } catch (_error) {
      toast.error('Failed to update certification');
    }
  };

  const handleDelete = async () => {
    if (!selectedCertification) return;

    try {
      await deleteMutation.mutateAsync(selectedCertification.id);
      toast.success('Certification deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedCertification(null);
    } catch (_error) {
      toast.error('Failed to delete certification');
    }
  };

  const hasActiveFilters = typeFilter || assetId || expiryStatus;

  const formatDate = (dateString: string) => {
    return formatDateOnly(dateString, '—', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getAssetName = (assetIdNum: number) => {
    const asset = assetsData?.results?.find((a) => a.id === assetIdNum);
    return asset ? asset.display_name : `Asset #${assetIdNum}`;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Certifications</h1>
          <p className="text-gray-600 mt-1">
            Manage equipment certifications and compliance
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {canAddCertifications && (
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add Certification
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Award className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Certifications</p>
                <p className="text-2xl font-bold">{totalCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Valid</p>
                <p className="text-2xl font-bold text-green-600">{validCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={expiredCount > 0 ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Expired</p>
                <p className={`text-2xl font-bold ${expiredCount > 0 ? 'text-red-600' : ''}`}>
                  {expiredCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select
              value={typeFilter || 'all'}
              onValueChange={(value) => updateSearchParams({ cert_type: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="CE">CE</SelectItem>
                <SelectItem value="ANSI">ANSI</SelectItem>
                <SelectItem value="CSA">CSA</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={assetId || 'all'}
              onValueChange={(value) => updateSearchParams({ asset: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Assets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assets</SelectItem>
                {assetsData?.results?.map((asset) => (
                  <SelectItem key={asset.id} value={String(asset.id)}>
                    {asset.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={expiryStatus || 'all'}
              onValueChange={(value) => updateSearchParams({ is_expired: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="false">Valid Only</SelectItem>
                <SelectItem value="true">Expired Only</SelectItem>
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-sm">
                <X className="h-4 w-4 mr-2" />
                Clear filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load certifications. Please try again.
            </div>
          ) : certifications.length === 0 ? (
            <div className="p-12 text-center">
              <Award className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No certifications found.</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Expiry Date</TableHead>
                  <TableHead>Issuer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {certifications.map((certification) => (
                  <TableRow
                    key={certification.id}
                    className={certification.is_expired ? 'bg-red-50' : ''}
                  >
                    <TableCell>
                      <span className="font-medium">
                        {certification.asset_name || getAssetName(certification.asset)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${typeColors[certification.cert_type]} w-fit`}>
                        {certification.cert_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">{formatDate(certification.issue_date)}</span>
                    </TableCell>
                    <TableCell>
                      <span className={certification.is_expired ? 'text-red-600 font-medium' : 'text-gray-600'}>
                        {formatDate(certification.expiry_date)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">{certification.issuer}</span>
                    </TableCell>
                    <TableCell>
                      {certification.is_expired ? (
                        <Badge className="bg-red-100 text-red-800 flex items-center gap-1 w-fit">
                          <AlertTriangle className="h-3 w-3" />
                          Expired
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                          <CheckCircle className="h-3 w-3" />
                          Valid
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {certification.document_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                          >
                            <a
                              href={resolveMediaUrl(certification.document_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(certification)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(certification)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => updateSearchParams({ page: String(Math.max(1, page - 1)) })}
                className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    isActive={page === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                onClick={() => updateSearchParams({ page: String(Math.min(totalPages, page + 1)) })}
                className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Certification</DialogTitle>
            <DialogDescription>
              Create a new certification for an asset.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="asset">Asset *</Label>
              <QuickAddSelect<Asset>
                value={selectedAsset}
                onChange={(asset) => {
                  setSelectedAsset(asset);
                  setFormData((current) => ({ ...current, asset: asset?.id ?? '' }));
                }}
                options={assetsData?.results ?? []}
                getOptionValue={(asset) => asset.id}
                getOptionLabel={(asset) => asset.display_name}
                getOptionDescription={(asset) => `${asset.serial_number} • ${asset.manufacturer} ${asset.model}`}
                placeholder="Select an asset"
                isLoading={isAssetsLoading}
                quickAdd={{
                  triggerLabel: '+ New asset',
                  dialogTitle: 'Create asset',
                  renderForm: ({ onCreated, onCancel }) => (
                    <InlineCreateAssetForm
                      onCreated={(asset) => {
                        onCreated(asset);
                      }}
                      onCancel={onCancel}
                    />
                  ),
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cert_type">Certification Type *</Label>
              <Select
                value={formData.cert_type}
                onValueChange={(value) => setFormData({ ...formData, cert_type: value as CertificationType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CE">CE</SelectItem>
                  <SelectItem value="ANSI">ANSI</SelectItem>
                  <SelectItem value="CSA">CSA</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="issue_date">Issue Date *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="issue_date"
                    type="date"
                    className="pl-10"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiry_date">Expiry Date *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="expiry_date"
                    type="date"
                    className="pl-10"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="issuer">Issuer *</Label>
              <Input
                id="issuer"
                placeholder="Enter issuer name"
                value={formData.issuer}
                onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="document_url">Document URL</Label>
              <Input
                id="document_url"
                type="url"
                placeholder="https://example.com/document.pdf"
                value={formData.document_url}
                onChange={(e) => setFormData({ ...formData, document_url: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Certification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Certification</DialogTitle>
            <DialogDescription>
              Update the certification details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-asset">Asset *</Label>
              <Select
                value={formData.asset ? String(formData.asset) : ''}
                onValueChange={(value) => setFormData({ ...formData, asset: parseInt(value, 10) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an asset" />
                </SelectTrigger>
                <SelectContent>
                  {assetsData?.results?.map((asset) => (
                    <SelectItem key={asset.id} value={String(asset.id)}>
                      {asset.display_name} ({asset.serial_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-cert_type">Certification Type *</Label>
              <Select
                value={formData.cert_type}
                onValueChange={(value) => setFormData({ ...formData, cert_type: value as CertificationType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CE">CE</SelectItem>
                  <SelectItem value="ANSI">ANSI</SelectItem>
                  <SelectItem value="CSA">CSA</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-issue_date">Issue Date *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="edit-issue_date"
                    type="date"
                    className="pl-10"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-expiry_date">Expiry Date *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="edit-expiry_date"
                    type="date"
                    className="pl-10"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-issuer">Issuer *</Label>
              <Input
                id="edit-issuer"
                placeholder="Enter issuer name"
                value={formData.issuer}
                onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-document_url">Document URL</Label>
              <Input
                id="edit-document_url"
                type="url"
                placeholder="https://example.com/document.pdf"
                value={formData.document_url}
                onChange={(e) => setFormData({ ...formData, document_url: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Certification</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this certification? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
